import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Paths } from "src/paths";
import { EngagementService } from "@services/engagements/engagementService";
import type { Launch } from "@models/launch.model";
import type { LaunchAdStatus } from "@models/launchAd";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { appStore } from "src/store/appStore";
import InfoTip from "@/shared/components/infoTip/InfoTip";
import SequenceCard from "./SequenceCard";
import TouchEditor from "./TouchEditor";
import ClientApprovalPreview from "./ClientApprovalPreview";
import SequencesCalendar from "./SequencesCalendar";
import HistoryPanel, { appendHistory, type HistoryEntry } from "./HistoryPanel";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import ApprovalFooter, { type CostRow } from "./ApprovalFooter";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import { PERSON_TOKENS } from "./touchCopy";
import { useItemRoute } from "../../../workflow/useItemRoute";
import { useCampaignSequences } from "./useCampaignSequences";
import { useCampaignKeyDates } from "./useCampaignKeyDates";
import { useCampaignAudiences } from "./useCampaignAudiences";
import { useCampaignChannels } from "./useCampaignChannels";
import { DeliveryService } from "@services/delivery/deliveryService";
import { resolveCopy, vocabularyOf } from "./touchCopy";
import SendToApprovalModal, { type PlaybookToSend } from "./SendToApprovalModal";
import ReviewChangesPanel, { type FlaggedTouch } from "./ReviewChangesPanel";
import { SequenceService } from "@services/sequence/sequenceService";
import { NotificationService } from "@services/notifications/NotificationService";
import { isAddedKey, toSequence, toSettings, touchIdFromKey } from "./sequenceAdapters";
import {
  RATE_EMAIL,
  RATE_SMS,
  sendDate,
  type Sequence,
} from "./emailSmsTypes";
import { blockFor } from "./touchBlocks";
import "./emailSms.scss";

type ShowFilter = "on" | "all";
type ViewMode = "list" | "calendar";

const money = (value: number) =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });


/*
    Where the catalog's sequences are switched on for one campaign and their triggers,
    audience and copy adjusted (MPN-2204). Senders and quiet hours stay on the campaign:
    they are account-level facts, and duplicating them here would give two places to set one
    From address.
*/
export default function EmailSmsStep() {
  const navigate = useNavigate();
  const { selectedEngagement } = useSnapshot(engagementStore);
  const { loggedInUser } = useSnapshot(appStore);
  const engagementId = selectedEngagement?.id ?? 0;

  const [campaigns, setCampaigns] = useState<Launch[]>([]);
  /*
      The chosen campaign lives in the URL, the way every other workflow step does it
      (MPN-2204). It was local state, so the switcher could not be linked to, a reload
      silently dropped back to the first campaign, and a QA step naming a campaign had no
      way to open it.
  */
  const [routeCampaignId, setRouteCampaignId] = useItemRoute();
  const activeCampaignId =
    routeCampaignId != null ? String(routeCampaignId) : String(campaigns[0]?.id ?? "");
  const setCampaignId = (value: string) => setRouteCampaignId(value ? Number(value) : null);

  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    void new EngagementService()
      .getEngagementLaunches(engagementId)
      .then((loaded) => {
        if (!cancelled) setCampaigns(loaded ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  const [show, setShow] = useState<ShowFilter>("on");
  const [view, setView] = useState<ViewMode>("list");
  const [editing, setEditing] = useState<string | null>(null);
  const [editingMode, setEditingMode] = useState<"preview" | "edit">("preview");

  const openTouch = (touchId: string, mode: "preview" | "edit" = "preview") => {
    setEditingMode(mode);
    setEditing(touchId);
  };
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [flagged, setFlagged] = useState<FlaggedTouch[]>([]);
  const [histOpen, setHistOpen] = useState(false);
  /*
      Starts empty and fills as somebody acts (MPN-2201). It was seeded with five edits
      attributed to two named people who never made them -- invented provenance is worse than
      none, because it reads as a record. A server-backed history for this screen is its own
      ticket; the engagement activity feed is engagement-wide and does not map onto these
      filters without inventing the mapping too.
  */
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const log = (entry: Omit<HistoryEntry, "who" | "when">) =>
    setHistory((prev) => appendHistory(prev, { ...entry, who: "You", when: "Just now" }));
  const launchId = activeCampaignId ? Number(activeCampaignId) : null;
  const {
    sequences: rows,
    approval,
    tokenValues,
    sendForApproval,
    isLoading: sequencesLoading,
    error: sequencesError,
    configure,
    overrideTouch,
    resetTouch,
    deleteAddedStep,
    setTouchStatus,
  } = useCampaignSequences(launchId);
  const [statusBusy, setStatusBusy] = useState<Set<string>>(new Set());
  const { keyDates, typeLabels, codeById } = useCampaignKeyDates(launchId);
  const audiences = useCampaignAudiences(engagementId, launchId);
  const { channels, emailChannel, sender } = useCampaignChannels(launchId);
  const [toast, setToast] = useState<string | null>(null);

  /*
      A real test send of the touch being edited (MPN-2206).

      The copy goes out RESOLVED, with this campaign's own token values: a test whose body
      still says `[first_name]` tells you nothing about what a student receives, which is the
      only question a test send answers.

      It goes from the internal test sender, never the client-branded one. A test arriving as
      the school is a message from the school, and the point is to check our own plumbing.
  */
  /** A row-menu test waiting to be confirmed, so nobody mails themselves by accident. */
  const [pendingTest, setPendingTest] = useState<{ touchId: string; to: string } | null>(null);

  /*
      What a per-recipient field resolves to in a test.

      A test has no recipient -- it goes to whoever typed their address in -- so `[FIRST
      NAME]` has nothing to fill it. The browser resolves the copy before sending, so it
      never reaches `render.person_values`, whose `FIRST_NAME_FALLBACK` is what a real send
      gives a student with no name on file. Without this the token resolved to an empty
      string and the test arrived reading "Hi ," which is worse than useless: it is the one
      thing a test is supposed to catch.
  */
  const testPersonValues = { "[FIRST NAME]": "there", "[LAST NAME]": "" };

  const sendTest = async (touchId: string, to: string) => {
    const touch = sequences.flatMap((s) => s.touches).find((row) => row.id === touchId);
    if (!touch) return;
    const testValues = { ...testPersonValues, ...previewValues };
    const service = new DeliveryService();
    try {
      if (touch.ch === "sms") {
        await service.testSms({
          to,
          body: resolveCopy(touch.text ?? "", testValues, vocabulary),
          organizationId: selectedEngagement?.client?.id ?? null,
        });
      } else {
        /*
            The EMAIL account, not merely the first active one. An organisation's accounts
            cover every channel — the first active one here was the direct-mail provider,
            and test-email rightly refuses it. Preferring the campaign's configured account
            keeps the test on the same plumbing the real send will use.
        */
        const accounts = await service.listAccounts(selectedEngagement?.client?.id ?? undefined);
        const emailAccounts = accounts.filter((a) => a.provider === "ses" && a.is_active);
        const configured = emailChannel?.provider_account_id;
        const account =
          emailAccounts.find((a) => a.id === configured) ?? emailAccounts[0];
        if (!account) {
          setToast("No email sending account for this organization yet.");
          return;
        }
        await service.testEmail(account.id, {
          to,
          // So the test goes out as the address this campaign is configured with. Without
          // it the server has no campaign to read and falls back to the platform's
          // transactional sender, which is what QA received (MPN-2206).
          launch_id: launchId,
          subject: resolveCopy(touch.subject ?? "", testValues, vocabulary),
          preheader: resolveCopy(touch.pre ?? "", testValues, vocabulary),
          html_b64: btoa(
            String.fromCharCode(
              ...new TextEncoder().encode(resolveCopy(touch.body ?? "", testValues, vocabulary)),
            ),
          ),
        });
      }
      setToast(`Test ${touch.ch === "sms" ? "text" : "email"} sent to ${to}`);
      log({ kind: "Test", what: `Sent a test ${touch.ch} to ${to}` });
    } catch {
      setToast("Could not send the test. Check the delivery setup.");
    }
  };

  /*
      Real values over sample ones (MPN-2207). The demo map stays underneath rather than
      being replaced: a token the registry cannot resolve for this campaign would otherwise
      render as a raw bracket in the Ops preview, which is a regression from what was there.
      The client preview passes `values` on its own and does not get the fallback.
  */
  const previewValues = tokenValues;

  /*
      The vocabulary the test send resolves against.

      Without it `resolveCopy` left a known-but-unvalued token standing as a bracket, so the
      test email a person actually received differed from the preview they had just read on
      the same screen -- and a test whose body still says [FIRST NAME] tells you nothing.
  */
  const { tokens: adTokens } = useAdTokens();
  const vocabulary = useMemo(
    () => vocabularyOf([...adTokens, ...PERSON_TOKENS]),
    [adTokens],
  );

  /*
      An edit after approval withdraws it (MPN-2207). The server does that unconditionally;
      this is the warning before it happens, because the cost is not obvious — the client has
      to look at the whole sequence again, and nothing sends in the meantime.

      One guard rather than six, so an edit path added later is covered by wrapping it rather
      than by remembering the rule.
  */
  const confirmEdit = useCallback(() => {
    if (approval.state !== "approved") return true;
    return window.confirm(
      "The client approved this copy. Changing it withdraws the approval and sends the " +
        "sequence back for review.",
    );
  }, [approval.state]);

  const sequences = useMemo(
    () => rows.map((row) => toSequence(row, codeById)),
    [rows, codeById],
  );

  const settings = useMemo(
    () => Object.fromEntries(rows.map((row) => [String(row.sequence_id), toSettings(row)])),
    [rows],
  );

  /*
      One row per switched-on playbook. The counts come from the resolved touches, so a
      channel the campaign has switched off is not offered to the client.
  */
  const toSend = useMemo<PlaybookToSend[]>(
    () =>
      sequences
        .filter((s) => settings[s.id]?.on)
        .map((s) => {
          const live = s.touches.filter(
            (touch) =>
              touch.on !== false &&
              (touch.ch === "email"
                ? settings[s.id]?.emailCh && channels.email
                : settings[s.id]?.smsCh && channels.sms),
          );
          return {
            sequenceId: Number(s.id),
            name: s.name,
            touches: live.length,
            email: live.filter((touch) => touch.ch === "email").length,
            sms: live.filter((touch) => touch.ch === "sms").length,
          };
        })
        .filter((p) => p.touches > 0),
    [sequences, settings],
  );

  /*
      The client's flagged touches and their comments, fetched per playbook because the
      review is per playbook. Only playbooks actually in Changes Requested are asked for.
  */
  useEffect(() => {
    const changed = (approval.playbooks ?? []).filter((p) => p.state === "changes");
    if (!launchId || changed.length === 0) {
      setFlagged([]);
      return;
    }
    let cancelled = false;
    void Promise.all(
      changed.map((p) =>
        new SequenceService()
          .getReview(launchId, p.sequence_id)
          .then((review) =>
            review.touches
              .filter((touch) => touch.verdict === "change")
              .map((touch) => ({
                ...touch,
                sequenceId: review.sequence_id,
                sequenceName: review.sequence_name,
                /* Edited since the client flagged it. `customized` is the resolver's signal
                   for "this campaign has changed it", which is what the design shows as
                   "Updated, ready to resubmit". */
                updated: !!sequences
                  .find((s) => Number(s.id) === review.sequence_id)
                  ?.touches.find((row) => touchIdFromKey(row.id) === touch.ad_id)?.customized,
              })),
          )
          .catch(() => [] as FlaggedTouch[]),
      ),
    ).then((lists) => {
      if (!cancelled) setFlagged(lists.flat());
    });
    return () => {
      cancelled = true;
    };
  }, [approval.playbooks, launchId, sequences]);

  const canResubmit = flagged.length > 0 && flagged.every((touch) => touch.updated);

  // The banners name whoever answered (MPN-2207). Older campaigns were reviewed before the
  // server recorded it, so both fall back to the wording they had.
  const reviewerSuffix = (approval.reviewer_name ?? "").trim();
  const reviewedOn = approval.reviewed_at
    ? new Date(approval.reviewed_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const handleResubmit = async () => {
    setIsSending(true);
    try {
      const ids = Array.from(new Set(flagged.map((touch) => touch.sequenceId)));
      const state = await sendForApproval(ids);
      log({ kind: "Approval", what: `Resubmitted ${state?.sent ?? 0} changed touches` });
      setChangesOpen(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const state = await sendForApproval(toSend.map((p) => p.sequenceId));
      // The client's email, rate limited server side to one an hour per engagement.
      await new NotificationService()
        .send_notification("CONTENT_REVIEW_REQUEST", { engagement: { id: engagementId } })
        .catch(() => undefined);
      log({
        kind: "Approval",
        what: `Sent to client approval: ${toSend.map((p) => p.name).join(", ")} · ${
          state?.sent ?? 0
        } touches`,
      });
      setSendOpen(false);
      setBannerDismissed(false);
    } finally {
      setIsSending(false);
    }
  };

  const visibleSequences = sequences.filter((s) => (show === "on" ? settings[s.id]?.on : true));
  const onCount = sequences.filter((s) => settings[s.id]?.on).length;

  // Next send across every sequence that is on, so the header answers "when does this
  // start" without expanding anything.
  const nextSend = useMemo(() => {
    const dates = sequences
      .filter((s) => settings[s.id]?.on)
      .flatMap((s) => s.touches.filter((t) => t.on !== false))
      .map((t) => sendDate(t, keyDates))
      .filter((d): d is Date => d != null && d.getTime() >= Date.now())
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0] ?? null;
  }, [sequences, settings, keyDates]);

  const activeCampaign = campaigns.find((c) => String(c.id) === activeCampaignId);
  const { costRows, costTotal } = useMemo(() => {
    const live = sequences.filter((s) => settings[s.id]?.on);
    const blockCtx = {
      keyDates,
      typeLabels,
      values: previewValues,
      campaignStart: activeCampaign?.campaign_start ?? null,
      campaignEnd: activeCampaign?.campaign_end ?? null,
    };
    /*
        A sequence reaches its own audience: the campaign's, or the separate one it was
        pointed at. Blocked touches are left out — a touch with no resolvable send date is
        not going to be delivered, so billing for it overstates the estimate (MPN-2204).
    */
    const sendable = (sequence: Sequence, channel: "email" | "sms") =>
      sequence.touches.filter(
        (touch) =>
          touch.ch === channel && touch.on !== false && blockFor(touch, blockCtx) === null,
      ).length;

    const reachOf = (sequence: Sequence) => {
      const setting = settings[sequence.id];
      if (setting?.audMode === "separate" && setting.audPick) {
        return audiences.reachById[Number(setting.audPick)] ?? 0;
      }
      return audiences.campaignReach;
    };

    let emailSends = 0;
    let smsSends = 0;
    let emailTouches = 0;
    let smsTouches = 0;
    live.forEach((sequence) => {
      const reach = reachOf(sequence);
      if (settings[sequence.id]?.emailCh && channels.email) {
        const n = sendable(sequence, "email");
        emailTouches += n;
        emailSends += reach * n;
      }
      if (settings[sequence.id]?.smsCh && channels.sms) {
        const n = sendable(sequence, "sms");
        smsTouches += n;
        smsSends += reach * n;
      }
    });

    const emailCost = emailSends * RATE_EMAIL;
    const smsCost = smsSends * RATE_SMS;
    const rows: CostRow[] = [
      {
        label: "Email",
        people: audiences.campaignReach.toLocaleString(),
        touches: String(emailTouches),
        rate: "$0.0001",
        cost: money(emailCost),
      },
      {
        label: "SMS",
        people: audiences.campaignReach.toLocaleString(),
        touches: String(smsTouches),
        rate: "$0.0083",
        cost: money(smsCost),
      },
    ];
    return { costRows: rows, costTotal: emailCost + smsCost };
    /* keyDates and audiences load after the first render. Without them here the estimate is
       computed against no key dates — every touch blocked, every cost $0.00 — and never
       recovers. */
  }, [sequences, settings, keyDates, typeLabels, previewValues, activeCampaign, audiences, channels]);


  const editingSequence = sequences.find((s) =>
    s.touches.some((t) => t.id === editing),
  );
  const editingTouch = editingSequence?.touches.find((t) => t.id === editing) ?? null;
  const editingSiblings = editingSequence?.touches ?? [];
  const editingCrumb = editingSequence
    ? `${editingSequence.name} › ${campaigns.find((c) => String(c.id) === activeCampaignId)?.name ?? "this campaign"}`
    : "";

  /*
      `itemId` is the campaign to open on that step. Without it the Campaign step lands on
      the list and asks which one — which is what "Add it in Key dates" did, from a touch
      that already knows the campaign it belongs to.
  */
  const goItem = (slug: string, itemId?: string) =>
    navigate(
      `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${slug}` +
        (itemId ? `/${itemId}` : ""),
    );

  return (
    <div className="esms">
      <div className="esms__head">
        <h5>Email &amp; SMS</h5>
        <InfoTip
          label="About Email and SMS"
          text="Results appear on Monitor once sending starts. Merge fields use [square_brackets], the same format as the video editor."
        />
        <span className="esms__spacer" />
        <HistoryButton onClick={() => setHistOpen(true)} title="Changes to this campaign's email and texts" />
        <label className="esms__campaign-label" htmlFor="esms-campaign">
          Campaign
        </label>
        <select
          id="esms-campaign"
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={activeCampaignId}
          onChange={(e) => setCampaignId(e.target.value)}
        >
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={String(campaign.id)}>
              {campaign.name}
            </option>
          ))}
        </select>
      </div>

      <div className="esms__intro">
        Turn on the catalog sequences this campaign uses, then adjust their triggers, audience
        and copy. Senders and quiet hours are set on the{" "}
        <button
          type="button"
          className="esms__link"
          onClick={() => goItem("campaign", activeCampaignId)}
        >
          campaign
        </button>
        .
      </div>

      <div className="campaign-label">
        Sequences
        <InfoTip
          label="Sequences"
          text="Students in more than one campaign may receive multiple sequences. Overlap rules TBD."
        />
        <span className="esms-count">
          {onCount} of {sequences.length} on
        </span>
        {nextSend && (
          <span style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0, color: "#334155", fontSize: 12 }}>
            Next send{" "}
            {nextSend.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        )}
      </div>

      <div className="esms__filters">
        <span>Show</span>
        <div className="btn-group btn-group-sm" role="group" aria-label="Which sequences to show">
          {(["on", "all"] as ShowFilter[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn btn-sm ${show === option ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setShow(option)}
            >
              {option === "on" ? "On" : "All"}
            </button>
          ))}
        </div>
        <span className="esms__spacer" />
        <div className="btn-group btn-group-sm" role="group" aria-label="List or calendar">
          {(["list", "calendar"] as ViewMode[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn btn-sm ${view === option ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setView(option)}
            >
              {option === "list" ? "List" : "Calendar"}
            </button>
          ))}
        </div>
      </div>

      {/* The client handed it back. Amber, named, and never behind a hover. */}
      {approval.state === "changes" && flagged.length > 0 && (
        <div className="esms-banner esms-banner--changes" role="status">
          <span className="esms-banner__dot">!</span>
          <span className="esms-banner__text">
            Changes requested{reviewerSuffix ? ` by ${approval.reviewer_name}` : ""} on{" "}
            {flagged.length} {flagged.length === 1 ? "touch" : "touches"} ·{" "}
            {flagged.filter((touch) => !touch.updated).length} left to update
          </span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setChangesOpen(true)}
          >
            Review changes
          </button>
        </div>
      )}

      {/* Approved, dismissible, and only once the client has actually answered. */}
      {approval.state === "approved" && !bannerDismissed && (
        <div className="esms-banner esms-banner--ok" role="status">
          <span className="esms-banner__dot">✓</span>
          <span className="esms-banner__text">
            Approved by {approval.reviewer_name || "the client"}
            {reviewedOn ? ` · ${reviewedOn}` : ""}. Touches will send on schedule.
          </span>
          <button
            type="button"
            className="esms-banner__x"
            aria-label="Dismiss"
            onClick={() => setBannerDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      {view === "calendar" ? (
        <SequencesCalendar
          sequences={sequences.filter((s) => settings[s.id]?.on)}
          keyDates={keyDates}
          onOpenTouch={(id) => openTouch(id, "preview")}
        />
      ) : (
      <div className="esms__list">
        {/*
            A write that failed must not take the screen away with it. The same state
            carries both kinds of failure: with nothing loaded it IS the screen, and over a
            list it is a banner above work the writer can still see and retry.
        */}
        {sequencesError && sequences.length > 0 && (
          <div className="esms__error" role="alert">{sequencesError}</div>
        )}
        {sequencesError && sequences.length === 0 ? (
          <div className="esms__empty">{sequencesError}</div>
        ) : sequencesLoading && sequences.length === 0 ? (
          <div className="esms__empty">Loading sequences…</div>
        ) : visibleSequences.length === 0 ? (
          <div className="esms__empty">
            No sequences on yet. Choose All to see every sequence and turn one on.
          </div>
        ) : (
          visibleSequences.map((sequence) => (
            <SequenceCard
              key={sequence.id}
              sequence={sequence}
              settings={settings[sequence.id]}
              keyDates={keyDates}
              typeLabels={typeLabels}
              campaignAudienceLabel={audiences.campaignAudienceLabel}
              smsAudienceLabel={audiences.smsAudienceLabel}
              audienceOptions={audiences.options}
              campaignEmailOn={channels.email}
              campaignSmsOn={channels.sms}
              values={previewValues}
              campaignStart={activeCampaign?.campaign_start ?? null}
              campaignEnd={activeCampaign?.campaign_end ?? null}
              onSettings={(next) => {
                if (!confirmEdit()) return;
                void configure(Number(sequence.id), {
                  is_on: next.on,
                  email_enabled: next.emailCh,
                  sms_enabled: next.smsCh,
                  audience_mode: next.audMode,
                  audience_id: next.audPick ? Number(next.audPick) : undefined,
                });
                if (next.on !== undefined) {
                  log({
                    kind: "Sequence",
                    what: `${next.on ? "Turned on" : "Turned off"} ${sequence.name}`,
                    key: `seq-${sequence.id}`,
                  });
                }
              }}
              onTouchChange={(touchId, next) => {
                const id = touchIdFromKey(touchId);
                if (id == null || isAddedKey(touchId)) return;
                if (!confirmEdit()) return;
                void overrideTouch(Number(sequence.id), id, {
                  is_on: next.on,
                  offset_days: next.days,
                  direction: next.dir,
                });
                const touch = sequence.touches.find((t) => t.id === touchId);
                if (!touch) return;
                log(
                  next.on !== undefined
                    ? {
                        kind: "Sequence",
                        what: `${next.on ? "Turned on" : "Turned off"} ${touch.title} (${touch.ch})`,
                        key: `touch-on-${touchId}`,
                      }
                    : {
                        kind: "Triggers",
                        what: `${touch.title}: trigger changed`,
                        key: `touch-trigger-${touchId}`,
                      },
                );
              }}
              onTouchStatus={(touchId, next) => {
                const touch = sequence.touches.find((t) => t.id === touchId);
                if (!touch?.launchAdId) return;
                if (!confirmEdit()) return;
                setStatusBusy((busy) => new Set(busy).add(touchId));
                void setTouchStatus(touch.launchAdId, next as LaunchAdStatus)
                  .then((ok) => {
                    if (!ok) return;
                    log({
                      kind: "Approval",
                      what: `${touch.title}: status set to ${next.toLowerCase().replace(/_/g, " ")}`,
                      key: `touch-status-${touchId}`,
                    });
                  })
                  .finally(() =>
                    setStatusBusy((busy) => {
                      const nextBusy = new Set(busy);
                      nextBusy.delete(touchId);
                      return nextBusy;
                    }),
                  );
              }}
              statusBusy={statusBusy}
              onPreviewTouch={openTouch}
              onOpenCatalog={() => undefined}
              onManageAudiences={() => goItem("audience")}
              onFixBlock={(target) =>
                target === "branding"
                  ? goItem("branding")
                  : goItem("campaign", activeCampaignId)
              }
              onResetTouch={(touchId) => {
                const id = touchIdFromKey(touchId);
                if (id == null || isAddedKey(touchId)) return;
                const touch = sequence.touches.find((t) => t.id === touchId);
                if (!confirmEdit()) return;
                void resetTouch(Number(sequence.id), id);
                if (touch) {
                  log({
                    kind: "Triggers",
                    what: `${touch.title}: reset to the catalog default`,
                    key: `reset-${touchId}`,
                  });
                }
              }}
              onDeleteTouch={(touchId) => {
                const id = touchIdFromKey(touchId);
                // Only a step this campaign added can be deleted; a catalog touch resets.
                if (id == null || !isAddedKey(touchId)) return;
                const touch = sequence.touches.find((t) => t.id === touchId);
                if (!confirmEdit()) return;
                void deleteAddedStep(Number(sequence.id), id);
                if (touch) {
                  log({ kind: "Sequence", what: `Deleted ${touch.title} from ${sequence.name}` });
                }
              }}
              onSendTest={(touchId) => {
                /*
                    Say where it is going before it goes. The v10 prototype has no Send test
                    on the row menu at all — only in the editor, behind a dialog naming the
                    address — and a menu item that silently mails the person who clicked it
                    gives no clue that anything happened, or to whom.
                */
                const to = loggedInUser?.email;
                if (!to) {
                  setToast("No email address on your account to send a test to.");
                  return;
                }
                setPendingTest({ touchId, to });
              }}
              onAddStep={() => undefined}
            />
          ))
        )}
      </div>
      )}

      <div className="esms__grow" />

      <ApprovalFooter
        costRows={costRows}
        costTotal={money(costTotal)}
        costContracted={money(1250)}
        costOverText={
          costTotal > 1250
            ? `The estimate is ${money(costTotal - 1250)} over the email and SMS lines on the contract.`
            : undefined
        }
        approval={approval.state}
        sendLabel={approval.state === "approved" ? "Approved" : "Send for approval"}
        sendDisabled={onCount === 0 || approval.state === "awaiting"}
        previewDisabled={onCount === 0}
        onPreview={() => setApprovalOpen(true)}
        onSend={() => setSendOpen(true)}
        onOpenContract={() => goItem("contract")}
      />

      {approvalOpen && (
        <ClientApprovalPreview
          campaignName={
            campaigns.find((c) => String(c.id) === activeCampaignId)?.name ?? "This campaign"
          }
          sequences={sequences.filter((s) => settings[s.id]?.on)}
          keyDates={keyDates}
          values={previewValues}
          fromName={sender.from_name}
          fromAddress={sender.from_address}
          smsSender={sender.sms_sender}
          footerAddress={sender.footer_address}
          approvalNote="Sending for approval locks this copy until the client answers. Edits after that send it back."
          sendLabel="Send for approval"
          sendDisabled={onCount === 0 || approval.state === "awaiting"}
          onSend={() => {
            setApprovalOpen(false);
            setSendOpen(true);
          }}
          onClose={() => setApprovalOpen(false)}
        />
      )}

      {changesOpen && (
        <ReviewChangesPanel
          flagged={flagged}
          canResubmit={canResubmit}
          isSending={isSending}
          onEditCopy={(_sequenceId, adId) => {
            setChangesOpen(false);
            // The editor keys touches by the resolver's row id, which carries the ad id.
            const row = sequences
              .flatMap((s) => s.touches)
              .find((touch) => touchIdFromKey(touch.id) === adId);
            if (row) openTouch(row.id, "edit");
          }}
          onResubmit={() => void handleResubmit()}
          onClose={() => setChangesOpen(false)}
        />
      )}

      {pendingTest && (
        <div
          className="esms-modal__scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Send test"
          onClick={() => setPendingTest(null)}
        >
          <div className="esms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="esms-modal__head">
              <h5>Send test</h5>
              <button
                type="button"
                className="esms-modal__x"
                aria-label="Close"
                onClick={() => setPendingTest(null)}
              >
                ×
              </button>
            </div>
            <div className="esms-modal__body">
              <p>
                This sends one real message to <strong>{pendingTest.to}</strong> — the address
                on your own account.
              </p>
              <p className="esms-modal__note">
                To send it somewhere else, open the message and use Send test there.
              </p>
            </div>
            <div className="esms-modal__foot">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setPendingTest(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  const { touchId, to } = pendingTest;
                  setPendingTest(null);
                  void sendTest(touchId, to);
                }}
              >
                Send to me
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="esms-toast" role="status" onAnimationEnd={() => setToast(null)}>
          {toast}
        </div>
      )}

      {sendOpen && (
        <SendToApprovalModal
          playbooks={toSend}
          isSending={isSending}
          onCancel={() => setSendOpen(false)}
          onSend={() => void handleSend()}
        />
      )}

      {histOpen && (
        <HistoryPanel
          campaignName={
            campaigns.find((c) => String(c.id) === activeCampaignId)?.name ?? "This campaign"
          }
          entries={history}
          onClose={() => setHistOpen(false)}
        />
      )}

      {editingTouch && (
        <TouchEditor
          touch={editingTouch}
          siblings={editingSiblings}
          keyDates={keyDates}
          crumb={editingCrumb}
          scope="campaign"
          initialMode={editingMode}
          values={previewValues}
          fromName={sender.from_name}
          fromAddress={sender.from_address}
          smsSender={sender.sms_sender}
          footerAddress={sender.footer_address}
          testFrom={sender.from_address}
          onSave={(next) => {
            const id = touchIdFromKey(editingTouch.id);
            if (id != null && editingSequence && !isAddedKey(editingTouch.id)) {
              if (!confirmEdit()) return;
              void overrideTouch(Number(editingSequence.id), id, {
                subject: next.subject,
                preheader: next.pre,
                html: next.body,
                cta_label: next.cta,
                sms_text: next.text,
              });
            }
            log({
              kind: "Copy",
              what: `${editingTouch.title} (${editingTouch.ch}): customized for this campaign`,
              key: `copy-${editingTouch.id}`,
            });
            setEditing(null);
          }}
          onReset={() => {
            const id = touchIdFromKey(editingTouch.id);
            if (id != null && editingSequence && !isAddedKey(editingTouch.id)) {
              if (!confirmEdit()) return;
              void resetTouch(Number(editingSequence.id), id);
            }
            setEditing(null);
          }}
          onSendTest={(to) => void sendTest(editing ?? "", to)}
          onStep={(id) => openTouch(id, "preview")}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
